import { capitalize, isUndefined } from 'lodash';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import type { RootStore } from '~/stores/root.store';
import { Page } from '~/models/page.model';
import {
    IAttributionContext,
    IBaseFlowContext,
    ICreateInvoiceBlockContext,
    ICreatePageContext,
    IDeletePageContext,
    IDuplicatePageContext,
    IDuplicatePageOptions,
    IUpdatePageContext,
    TBlock,
    TCommand
} from '~/types/builder.types';
import { Action, ServerActionType } from '~/services/actions-manager/action';
import { ActionName } from '~/services/actions-manager/action-name';
import {
    createCompanyInfoBlockOnPage,
    doesContainLinkedQuestion
} from '~/utils/block.utils';
import {
    calculatePageOrder,
    createPage,
    createPageForCharge,
    createRevertForCreateCommands,
    deletePage,
    duplicatePage,
    getDuplicatePageOrder,
    getNewPageOrder,
    updatePage
} from '~/utils/page.utils';
import {
    calculatePageBaseDataForServer,
    calculatePageWithAttributionDataForServer,
    createCreateBlockCommand,
    createCreatePageCommand,
    createDeleteBlockCommand
} from '~/utils/commands.utils';
import { BaseBlock } from '~/models/blocks/base-block.model';
import type { IFlowTemplate, TBlockType } from '~/types/model.types';
import { FlowActionGroup } from '~/services/actions-manager/FlowActionGroup';
import {
    ICreateBlockOptions,
    ICreatePageAndBlockProps
} from '~/controllers/block.controller';
import type { Flow } from '~/models/flow.model';
import {
    checkLimitations,
    checkNewPageWithBlocksCreationRestrictions,
    notBlankBlockCreationLimitations
} from '~/components/Builder/Common/CreationLimitations/creationLimitations';
import { FlowAction } from '~/services/actions-manager/flow-action';
import { PageOutboundEvents } from '~/components/Builder/Mobile/builderMobileApi.enums';
import {
    BlockLike,
    PossibleRestrictions
} from '~/components/Builder/Common/CreationLimitations/creationLimitations.types';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import useAnalytics from '~/common/useAnalytics';

export const MAX_PAGES = 30;
export const MIN_PAGES = 1;

export interface ICreatePageActionProps {
    pageTitle?: string;
    order?: number;
    shouldSelectNewPage?: boolean;
}
export interface IPageActionDescriptionProps {
    actionType?: string;
    pageTitle?: string;
    blockType?: TBlockType;
    order?: number;
    pageOriginId?: string;
    isManual?: boolean;
}

interface IImportPagesOptions {
    shouldAddChargeBlockManually?: boolean;
    creationLimitationsRestrictions: Record<
        string,
        PossibleRestrictions<
            typeof notBlankBlockCreationLimitations,
            keyof typeof notBlankBlockCreationLimitations
        >
    >;
}

const ImportPagesOptionsDefaults: IImportPagesOptions = {
    shouldAddChargeBlockManually: false,
    creationLimitationsRestrictions: {}
};

export class PageController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    createPage(pageTitle?: string, order?: number) {
        const {
            actionsManager,
            flowCtrl,
            builderUiStore: { selectedFlow, setShowPagesOverlayForId }
        } = this.rootStore;

        if (selectedFlow.pages.length < MAX_PAGES) {
            const createPageAction = this.createCreatePageAction({
                pageTitle,
                order
            });
            actionsManager.doNewApplyAction(createPageAction);
            flowCtrl.resetSelectedBlock();
            setShowPagesOverlayForId(null);
        }
    }

    duplicatePage(page, options: IDuplicatePageOptions = {}) {
        const {
            actionsManager,
            builderUiStore: { selectedFlow },
            toastStore,
            eventEmitterCtrl
        } = this.rootStore;

        if (selectedFlow.pages.length < MAX_PAGES) {
            const duplicatePageAction = this.createDuplicatePageAction(
                page,
                options
            );
            actionsManager.doNewApplyAction(duplicatePageAction);

            // If duplicated page contains a linked question, show toast
            if (doesContainLinkedQuestion(page.rootBlock)) {
                toastStore.add({
                    type: ToastType.Info,
                    text: 'The duplicated page contained linked questions. Please note that we have copied these questions to the new page  - but unlinked them',
                    autoDismissMs: 12000
                });
            }

            eventEmitterCtrl.trigger(PageOutboundEvents.PagesList, {});
        }
    }

    importPages(
        pages: Array<Page>,
        {
            shouldAddChargeBlockManually,
            creationLimitationsRestrictions = ImportPagesOptionsDefaults.creationLimitationsRestrictions
        }: IImportPagesOptions = ImportPagesOptionsDefaults
    ) {
        const {
            actionsManager,
            builderUiStore,
            builderUiStore: { selectedFlow, selectedPage },
            blockCtrl
        } = this.rootStore;

        let firstPageId = null;
        let firstOldSelectedPageId = null;

        actionsManager.groupActions((action: FlowActionGroup) => ({
            name: ActionName.ImportPages,
            description: this.getPageActionDescription({
                actionType: 'import'
            }),
            serverActionType: ServerActionType.CreatePage,
            groupActions: () => {
                const groupedDuplicatePageAction =
                    action.actionAs<FlowAction<IDuplicatePageContext>>();

                pages.forEach(page => {
                    const creationLimitationRestriction =
                        creationLimitationsRestrictions[page.page_id];

                    const order = creationLimitationRestriction
                        ? this.getNewImportedPageOrderFromRestriction(
                              selectedFlow,
                              creationLimitationRestriction
                          )
                        : Math.max(...selectedFlow.pages.map(p => p.order)) + 1;

                    this.duplicatePage(page, {
                        order,
                        isImporting: true,
                        withAttributions: true
                    });

                    const { newPageId, oldSelectedPageId } =
                        groupedDuplicatePageAction.ctx;

                    firstPageId = firstPageId || newPageId;
                    firstOldSelectedPageId =
                        firstOldSelectedPageId || oldSelectedPageId;
                });

                const isInvoiceBlockInPages = pages.some(
                    page => page.isInvoicePage
                );

                if (selectedFlow.isLeadForm && isInvoiceBlockInPages) {
                    blockCtrl.changeInvoiceForPublicFlow(selectedFlow);
                }

                if (shouldAddChargeBlockManually) {
                    const chargeBlockAction = this.createInvoiceBlockAction(
                        selectedPage.rootBlock,
                        -1,
                        {},
                        true
                    );

                    actionsManager.doNewApplyAction(chargeBlockAction);
                }

                groupedDuplicatePageAction.setContext({
                    newPageId: firstPageId,
                    oldSelectedPageId: firstOldSelectedPageId
                });

                builderUiStore.selectedFlow.blocks?.forEach(block =>
                    block.afterActionsApplied()
                );
            },
            applySideEffects: () =>
                this.applyCreatePageSideEffects(
                    action as unknown as Action<ICreatePageContext>
                ),
            createApplyDataForServer: () =>
                this.getAttributedDataForServer(
                    action.actionAs<FlowAction<IDuplicatePageContext>>()
                ),
            revertSideEffects: () =>
                this.revertCreatePageSideEffects(
                    action as unknown as Action<ICreatePageContext>
                )
        }));
    }

    importPagesToCurrentFlow({
        pagesToImport,
        creationLimitationsRestrictions
    }: {
        pagesToImport: Page[];
        creationLimitationsRestrictions?: Record<
            string,
            PossibleRestrictions<
                typeof notBlankBlockCreationLimitations,
                keyof typeof notBlankBlockCreationLimitations
            >
        >;
    }) {
        const {
            flowCtrl,
            toastStore,
            builderUiStore: { setShowPagesOverlayForId }
        } = this.rootStore;

        try {
            const shouldAddChargeBlockManually =
                this.addChargeBlockWithInvoice(pagesToImport);

            this.importPages(pagesToImport, {
                shouldAddChargeBlockManually,
                creationLimitationsRestrictions
            });
            flowCtrl.resetSelectedBlock();
            setShowPagesOverlayForId(null);

            const toastText =
                pagesToImport.length > 1
                    ? `${pagesToImport.length} pages from ${pagesToImport[0].flow.title} were added successfully
                    and the current theme was applied.
                    Any changes made will only apply here.`
                    : `${pagesToImport[0].title} was added successfully
                    and the current theme was applied. Any changes will only apply here.`;

            toastStore.add({
                type: ToastType.Success,
                text: toastText
            });
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: `Something went wrong...the selected page could not be added`
            });

            throw error;
        }
    }

    private addChargeBlockWithInvoice(pagesToImport: Page[]) {
        let invoicePage = null;
        let chargePage = null;

        pagesToImport.forEach(currPage => {
            if (currPage.isInvoicePage) {
                invoicePage = currPage;
            }

            if (currPage.actionBlock?.type === 'charge_container_block') {
                chargePage = currPage;
            }
        });

        if (invoicePage && !chargePage) {
            chargePage = invoicePage.flow.pages.find(
                currPage =>
                    currPage.actionBlock?.type === 'charge_container_block'
            );

            if (!chargePage) {
                // in case of block template
                return true;
            }

            pagesToImport.push(chargePage);
        }

        return false;
    }

    async loadOnePageFromFlow(
        template: IFlowTemplate,
        track?: ReturnType<typeof useAnalytics>['track']
    ) {
        const { flowCtrl, flowStore } = this.rootStore;

        await flowCtrl.fetchFlowToImport(template._id);

        const flow = flowStore.flows[template._id];

        const page = flow.pages[0];

        this.importSinglePageFromFlow(page, track);
    }

    private importSinglePageFromFlow(
        page: Page,
        track: ReturnType<typeof useAnalytics>['track']
    ) {
        const { toastStore } = this.rootStore;

        // We can't disable the flow in advance because its blocks are only fetched after clicking it
        const limitation = checkLimitations(notBlankBlockCreationLimitations, {
            sourceBlock: page.rootBlock,
            targetFlow: this.rootStore.builderUiStore.selectedFlow,
            targetPage: null,
            targetBlock: page.rootBlock as BlockLike
        });

        if (limitation.status === 'disabled') {
            toastStore.add({
                type: ToastType.Error,
                text: limitation.reason
            });
            track(AnalyticsEvents.ImportPageFromFlow, {
                action: 'error',
                error_type: 'limitations',
                reason: limitation.reason
            });
            return;
        }

        this.importPagesToCurrentFlowWithUserApprovalOfRestrictions({
            pagesToImport: [page]
        });
    }

    private getNewImportedPageOrderFromRestriction(
        flow: Flow,
        creationLimitationRestriction: PossibleRestrictions<
            typeof notBlankBlockCreationLimitations,
            keyof typeof notBlankBlockCreationLimitations
        >
    ) {
        const { issueType, solutionData } = creationLimitationRestriction;

        switch (issueType) {
            case 'forbidServiceBlockAfterInvoiceBlock':
            case 'forbidServiceBlockAfterContractBlockWithServiceRecaps':
            case 'forbidMoreThanOneActionBlockInPage':
            case 'forbidSessionSelectionBlockAfterSchedulerBlock':
            case 'forbidInvoiceBeforeServiceSelection': {
                return solutionData.newPageOrder;
            }
            case 'forbidContractWithRecapsBeforeServiceSelection':
            case 'chargeBlockCanOnlyBeOnLastPageInPublicFile': {
                const { dropTargetId, direction } = solutionData;

                return calculatePageOrder({
                    pages: flow.pages,
                    relativeToPage: flow.pagesMap[dropTargetId],
                    shouldAddBeforePage: direction === 'top'
                });
            }
        }
    }

    getPageActionDescription({
        actionType,
        pageTitle,
        order,
        blockType,
        pageOriginId,
        isManual
    }: IPageActionDescriptionProps) {
        let description = `${capitalize(actionType)} a page`;

        if (order) {
            description += ` of order ${order}`;
            if (pageTitle) {
                description += ` and`;
            }
        }

        if (pageTitle) {
            description += ` with page title ${pageTitle}`;
        }

        if (blockType) {
            description += ` and also a block of ${blockType.replace(
                /_/g,
                ' '
            )}`;
        }

        if (pageOriginId) {
            description += ` of page id ${pageOriginId}`;
        }

        if (isManual) {
            description += ' manually';
        }

        return description;
    }

    updatePage(
        pageId: string,
        data: Partial<Page>,
        oldData: Partial<Page> = null
    ) {
        const { actionsManager } = this.rootStore;

        const updatePageAction = this.createUpdatePageAction(
            pageId,
            data,
            oldData
        );
        actionsManager.doNewApplyAction(updatePageAction);
    }

    deletePage(page: Page) {
        const {
            builderUiStore: { selectedFlow },
            eventEmitterCtrl
        } = this.rootStore;

        if (selectedFlow.draftRevision.pageIds.length > MIN_PAGES) {
            const { actionsManager } = this.rootStore;

            actionsManager.groupActions(() => ({
                name: ActionName.DeletePage,
                serverActionType: ServerActionType.DeletePage,
                groupActions: () => {
                    const deletePageAction = this.createDeletePageAction(page);
                    actionsManager.doNewApplyAction(deletePageAction);
                }
            }));

            eventEmitterCtrl.trigger(PageOutboundEvents.PagesList, {});
        }
    }

    movePageRelativeToPage({
        relativeToPageId,
        pageIdToMove,
        direction
    }: {
        relativeToPageId: string;
        pageIdToMove: string;
        direction: 'top' | 'bottom';
    }) {
        const {
            builderUiStore: {
                selectedFlow: { pages, pagesMap }
            }
        } = this.rootStore;
        const relativeToPage = pagesMap[relativeToPageId];
        const pageToMove = pagesMap[pageIdToMove];

        this.updatePage(pageIdToMove, {
            order: getNewPageOrder({
                relativeToPage,
                pageToMove,
                pages,
                direction
            })
        });
    }

    updatePageLocally(flowId: string, pageId: string, data: Partial<Page>) {
        this.rootStore.flowStore.flows[flowId]?.draftRevision.setPageData(
            pageId,
            data
        );
    }

    createPageAndBlockAction<T extends TBlockType>({
        blockType,
        initialAttributes,
        blockOptions,
        pageOptions,
        applySideEffects,
        revertSideEffects
    }: ICreatePageAndBlockProps<T> & {
        applySideEffects?: () => void;
        revertSideEffects?: () => void;
    }) {
        const { builderUiStore, actionsManager, blockCtrl } = this.rootStore;

        actionsManager.groupActions((action: FlowActionGroup) => ({
            name: ActionName.CreateCreatePageAndBlock,
            description: this.getPageActionDescription({
                actionType: 'create',
                pageTitle: pageOptions.pageTitle,
                blockType
            }),
            serverActionType: ServerActionType.CreatePage,
            applySideEffects,
            revertSideEffects,
            onApplyToServerResolved: () => {},
            onRevertToServerResolved: () =>
                blockCtrl.removeBlockVariables(action),
            groupActions: () => {
                if (builderUiStore.selectedFlow.pages.length >= MAX_PAGES) {
                    return;
                }

                const createPageAction =
                    this.createCreatePageAction(pageOptions);

                actionsManager.doNewApplyAction(createPageAction);

                const { rootBlock } =
                    builderUiStore.selectedFlow.revision.pagesMap[
                        createPageAction.ctx.newPageId
                    ];

                blockCtrl.createBlock({
                    block: rootBlock,
                    blockType,
                    direction: 'bottom',
                    isParent: true,
                    options: blockOptions,
                    initialAttributes
                });
            }
        }));
    }

    createPageAndBlockRelativeToPage<T extends TBlockType>({
        relativeToPage,
        shouldAddBeforePage,
        blockType,
        pageTitle,
        blockOptions,
        initialAttributes,
        applySideEffects,
        revertSideEffects
    }: {
        relativeToPage: Page;
        shouldAddBeforePage?: boolean;
        blockType: T;
        pageTitle: string;
        blockOptions?: ICreateBlockOptions;
        initialAttributes?: Partial<Extract<TBlock, { type: T }>>;
        applySideEffects?: () => void;
        revertSideEffects?: () => void;
    }) {
        const { builderUiStore } = this.rootStore;

        // calc page order
        const order = calculatePageOrder({
            pages: builderUiStore.selectedFlow.pages,
            relativeToPage,
            shouldAddBeforePage
        });

        // create the page and the block
        this.createPageAndBlockAction({
            blockType,
            pageOptions: {
                pageTitle,
                order,
                shouldSelectNewPage: false
            },
            blockOptions,
            initialAttributes,
            applySideEffects,
            revertSideEffects
        });
    }

    // #region actions
    createCreatePageAction({
        pageTitle,
        order,
        shouldSelectNewPage
    }: ICreatePageActionProps) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<ICreatePageContext>(
            (newAction: Action<ICreatePageContext>) => ({
                name: ActionName.CreatePage,
                description: this.getPageActionDescription({
                    actionType: 'create',
                    pageTitle,
                    order,
                    isManual: true
                }),
                serverActionType: ServerActionType.CreatePage,
                ctx: {
                    flow: builderUiStore.selectedFlow
                },
                createCommands: () =>
                    this.createPageCreateCommands(newAction, pageTitle, order),
                applySideEffects: () =>
                    this.applyCreatePageSideEffects(
                        newAction,
                        shouldSelectNewPage
                    ),
                revertSideEffects: () =>
                    this.revertCreatePageSideEffects(newAction),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    createDuplicatePageAction(
        page: Page,
        { order, isImporting }: IDuplicatePageOptions = {}
    ) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<IDuplicatePageContext>(
            (newAction: Action<IDuplicatePageContext>) => ({
                name: ActionName.DuplicatePage,
                description: this.getPageActionDescription({
                    actionType: 'duplicate',
                    pageOriginId: page.page_id
                }),
                serverActionType: ServerActionType.CreatePage,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    page,
                    order,
                    isImporting,
                    source_page_id: page.page_id,
                    source_flow_id: page.flow_id
                },
                createCommands: () =>
                    this.createDuplicatePageCommands(newAction),
                applySideEffects: () =>
                    this.applyCreatePageSideEffects(newAction),
                revertSideEffects: () =>
                    this.revertCreatePageSideEffects(newAction),
                createApplyDataForServer: () =>
                    this.getAttributedDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    createUpdatePageAction(
        pageId: string,
        data: Partial<Page>,
        oldData: Partial<Page> = null
    ) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<IUpdatePageContext>(
            (newAction: Action<IUpdatePageContext>) => ({
                name: ActionName.UpdatePage,
                description: this.getPageActionDescription({
                    actionType: 'update',
                    pageOriginId: pageId
                }),
                serverActionType: ServerActionType.UpdatePage,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    data: {
                        page_id: pageId,
                        ...data
                    },
                    oldData
                },
                createCommands: () => this.createUpdatePageCommands(newAction),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    createDeletePageAction(page: Page) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<IDeletePageContext>(
            (newAction: Action<IDeletePageContext>) => ({
                name: ActionName.DeletePage,
                description: this.getPageActionDescription({
                    actionType: 'delete',
                    pageOriginId: page.page_id
                }),
                serverActionType: ServerActionType.DeletePage,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: page.page_id,
                    rootBlockId: page.root_block_id
                },
                createCommands: () => this.createDeletePageCommands(newAction),
                applySideEffects: () =>
                    this.applyDeletePageSideEffects(newAction),
                revertSideEffects: () =>
                    this.revertDeletePageSideEffects(newAction),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    createInvoiceBlockAction(
        block: TBlock,
        order: number,
        initialAttributes: any,
        onlyChargeBlock: boolean = false,
        options: ICreateBlockOptions = {}
    ) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<ICreateInvoiceBlockContext>(
            (newAction: Action<ICreateInvoiceBlockContext>) => ({
                name: ActionName.CreateInvoiceBlock,
                serverActionType: ServerActionType.CreatePage,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    blockType: 'invoice_container_block',
                    order,
                    initialAttributes,
                    onlyChargeBlock
                },
                createCommands: () =>
                    this.createInvoiceBlockCommands(newAction),
                applySideEffects: () => {
                    this.applyInvoiceSideEffect(newAction, options);
                },
                revertSideEffects: () =>
                    this.revertCreatePageSideEffects(newAction),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    importPagesToCurrentFlowWithUserApprovalOfRestrictions({
        pagesToImport
    }: {
        pagesToImport: Page[];
    }) {
        const {
            builderUiStore: { setCreationLimitDialogDetails, selectedFlow }
        } = this.rootStore;

        const creationLimitationsRestrictionsByPage =
            checkNewPageWithBlocksCreationRestrictions({
                flow: selectedFlow,
                pagesToAdd: pagesToImport
            });

        const creationLimitationsRestrictions = Object.values(
            creationLimitationsRestrictionsByPage
        ).filter(x => x);
        if (!creationLimitationsRestrictions.length) {
            this.importPagesToCurrentFlow({
                pagesToImport
            });

            return;
        }

        const {
            reason: { short, long, cta, rejectCta }
        } = creationLimitationsRestrictions[0];

        const resolution = () => {
            this.importPagesToCurrentFlow({
                pagesToImport,
                creationLimitationsRestrictions:
                    creationLimitationsRestrictionsByPage
            });
        };

        if (creationLimitationsRestrictions.length === 1) {
            setCreationLimitDialogDetails({
                title: short,
                description: long,
                approveCTA: {
                    text: cta,
                    callback: resolution
                },
                rejectCTA: { text: rejectCta }
            });

            return;
        }

        setCreationLimitDialogDetails({
            approveCTA: {
                text: `ADD ${pagesToImport.length} PAGES`,
                callback: resolution
            },
            description: `${pagesToImport.length} pages will be added to your file-they're\norganized to create a seamless clientflow.`,
            rejectCTA: { text: 'DO NOT ADD' },
            title: 'Optimized page order'
        });
    }

    private createInvoiceBlockCommands(
        action: Action<ICreateInvoiceBlockContext>
    ) {
        const {
            blockCtrl,
            builderUiStore,
            builderUiStore: {
                selectedPageId,
                selectedFlow: { isLeadForm, lastPage }
            },
            companyStore: { currCompany }
        } = this.rootStore;
        const { flow, blockId, initialAttributes, onlyChargeBlock } =
            action.ctx;
        const parentBlock = flow.draftRevision.blocksMap[blockId];

        let commands: TCommand[] = [];
        let revertCommands: TCommand[] = [];
        let newTargetBlock = null;

        if (!onlyChargeBlock) {
            const {
                parentOldChildrenLayout,
                newBlocksMap,
                newTargetBlock: newInvoiceTargetBlock,
                commands: createInvoiceBlockCommands
            } = blockCtrl.createApplyCreateBlocksCommands(
                parentBlock,
                'invoice_container_block',
                action.ctx,
                null,
                initialAttributes
            );

            const revertInvoicePageCommands =
                blockCtrl.createRevertCreateBlockCommands(
                    newBlocksMap,
                    parentBlock,
                    parentOldChildrenLayout
                );

            commands = commands.concat(createInvoiceBlockCommands);
            revertCommands = revertCommands.concat(revertInvoicePageCommands);
            newTargetBlock = newInvoiceTargetBlock;
        }

        const pageToCreateAfter = isLeadForm
            ? lastPage
            : newTargetBlock?.page || builderUiStore.selectedPage;

        const {
            newPage,
            rootBlockOfPage,
            newBlocksMap: createChargeContainerBlocksMaps
        } = createPageForCharge(
            action.ctx.flow._id,
            action.ctx.flow.draftRevision,
            builderUiStore.selectedFlow.pages,
            pageToCreateAfter,
            this.rootStore
        );

        const createdPageCommands = [
            createCreateBlockCommand(rootBlockOfPage),
            createCreatePageCommand(newPage)
        ];

        const createdCompanyCommands = createCompanyInfoBlockOnPage(
            action.ctx.flow,
            rootBlockOfPage.block_id,
            rootBlockOfPage,
            blockCtrl,
            currCompany
        );

        const createdChargeContainerCreateCommands: TCommand[] = Object.values(
            createChargeContainerBlocksMaps
        ).map(newBlock => createCreateBlockCommand(newBlock));

        const chargePageCommands = [
            ...createdPageCommands,
            ...createdCompanyCommands,
            ...createdChargeContainerCreateCommands
        ];

        commands = commands.concat(chargePageCommands);

        const { revertCommands: revertChargePageCommands } =
            this.createRevertPageCreateCommands(chargePageCommands);

        revertCommands = revertCommands.concat(revertChargePageCommands);

        action.setContext({
            commands,
            revertCommands,
            newPageId: newPage.page_id,
            oldSelectedPageId: selectedPageId,
            newTargetBlock
        });
    }

    createPageCreateCommands(
        action: Action<ICreatePageContext>,
        pageTitle?: string,
        newPageOrder?: number
    ) {
        const {
            builderUiStore: {
                selectedFlow,
                selectedFlow: { isLeadForm, hasChargeBlock, pageWithPay },
                selectedPageId
            }
        } = this.rootStore;

        let order = newPageOrder;

        if (order == null) {
            if (isLeadForm && hasChargeBlock) {
                order = calculatePageOrder({
                    pages: selectedFlow.pages,
                    relativeToPage: pageWithPay,
                    shouldAddBeforePage: true
                });
            } else {
                order = Math.max(...selectedFlow.pages.map(p => p.order)) + 1;
            }
        }

        const commands = createPage(
            {
                order,
                title: pageTitle
            },
            action.ctx.flow._id,
            selectedFlow.draftRevision,
            this.rootStore
        );

        // Add new page to selected flow
        const { newPageId, revertCommands } =
            this.createRevertPageCreateCommands(commands);

        action.setContext({
            commands,
            revertCommands,
            newPageId,
            oldSelectedPageId: selectedPageId
        });
    }

    private applyCreatePageSideEffects(
        action: Action<ICreatePageContext>,
        shouldSelectNewPage = true
    ) {
        const {
            builderUiStore: { selectPage, selectedFlow }
        } = this.rootStore;
        const { newPageId } = action.ctx;

        if (shouldSelectNewPage) {
            selectPage(newPageId);
        }

        selectedFlow.revision.pagesMap[newPageId]?.blocks?.forEach(block =>
            block.onBlockCreated()
        );
    }

    private revertCreatePageSideEffects(action: Action<ICreatePageContext>) {
        const {
            builderUiStore: { selectPage, selectedFlow }
        } = this.rootStore;
        const { newPageId, oldSelectedPageId } = action.ctx;

        selectedFlow.draftRevision.removePage(newPageId);

        if (!oldSelectedPageId) {
            return;
        }

        selectPage(oldSelectedPageId);
    }

    createDuplicatePageCommands(action: Action<IDuplicatePageContext>) {
        const {
            builderUiStore: { selectedPageId }
        } = this.rootStore;

        const { ctx } = action;
        const { pages } = ctx.flow.draftRevision;
        const { order, page, flow, isImporting } = ctx;

        const newOrder = isUndefined(order)
            ? getDuplicatePageOrder(page.page_id, pages)
            : order;

        const { commands, createdBlocks } = duplicatePage(
            page,
            newOrder,
            flow._id,
            flow.draftRevision,
            this.rootStore,
            { isImporting, withAttributions: true }
        );

        const { newPageId, newRootBlockId, revertCommands } =
            this.createRevertPageCreateCommands(commands);

        createdBlocks.forEach(createdBlock => {
            if (createdBlock.block_id === newRootBlockId) {
                return;
            }

            revertCommands.push(
                createDeleteBlockCommand(createdBlock.block_id)
            );
        });

        action.setContext({
            commands,
            revertCommands,
            oldSelectedPageId: selectedPageId,
            newPageId
        });
    }

    private createRevertPageCreateCommands(commands: TCommand[]) {
        const newPage = <Page>(
            commands.find(({ data }) => data instanceof Page).data
        );
        const pageRootBlock = <TBlock>(
            commands.find(
                ({ data }) =>
                    (<BaseBlock>data).block_id === newPage.root_block_id
            ).data
        );
        const newPageId = newPage.page_id;

        const revertCommands = createRevertForCreateCommands(commands);

        return {
            newPageId,
            newRootBlockId: pageRootBlock.block_id,
            revertCommands
        };
    }

    createUpdatePageCommands(action: Action<IUpdatePageContext>) {
        const { commands, revertCommands } = updatePage(
            action.ctx.flow.draftRevision.pagesMap[action.ctx.data.page_id],
            action.ctx.data,
            action.ctx.oldData
        );

        action.setContext({ commands, revertCommands });
    }

    createDeletePageCommands(action: Action<IDeletePageContext>) {
        const { commands, revertCommands } = deletePage(
            action.ctx.pageId,
            action.ctx.rootBlockId,
            this.rootStore
        );

        action.setContext({ commands, revertCommands });
    }

    private applyDeletePageSideEffects(action: Action<IDeletePageContext>) {
        const {
            builderUiStore: { selectedFlow, selectPage, selectedPageId }
        } = this.rootStore;

        // Deleting the selected page should auto select the next page
        if (selectedPageId === action.ctx.pageId) {
            const selectedPageIndex =
                selectedFlow.draftRevision.pageIds.indexOf(action.ctx.pageId);
            const nextSelectedPage =
                selectedPageIndex + 1 ===
                selectedFlow.draftRevision.pageIds.length
                    ? selectedPageIndex - 1
                    : selectedPageIndex + 1;
            selectPage(selectedFlow.draftRevision.pageIds[nextSelectedPage]);
        }
    }

    revertDeletePageSideEffects(action: Action<IDeletePageContext>) {
        const {
            builderUiStore: { selectPage }
        } = this.rootStore;

        selectPage(action.ctx.pageId);
    }

    private getBaseDataForServer(action: Action<IBaseFlowContext>) {
        const {
            ctx: { commands }
        } = action;

        return calculatePageBaseDataForServer(commands, action);
    }

    private getAttributedDataForServer(action: Action<IAttributionContext>) {
        const {
            ctx: { commands }
        } = action;

        return calculatePageWithAttributionDataForServer(commands, action);
    }

    private getRevertBaseDataForServer(action: Action<IBaseFlowContext>) {
        const {
            ctx: { revertCommands }
        } = action;

        return calculatePageBaseDataForServer(revertCommands, action);
    }

    private applyInvoiceSideEffect(
        action: Action<ICreateInvoiceBlockContext>,
        options?: ICreateBlockOptions
    ) {
        const { blockCtrl } = this.rootStore;
        this.applyCreatePageSideEffects(action, false);
        blockCtrl.applyCreateBlockSideEffects({ action, options });
    }

    // #endregion
}
